export default IndexRoute;

import { ArrowUpRightIcon } from '@heroicons/react/20/solid';
import { CheckBadgeIcon } from '@heroicons/react/24/outline';
import { useEffect } from 'react';
import { InView } from 'react-intersection-observer';
import { navigate } from 'vike/client/router';

import AppStoreBadges from '@/components/shared/AppStoreBadges';
import SubscribeExternalLink from '@/components/shared/SubscribeExternalLink';
import { CFBIcon, NFLIcon } from '@/pages/order/components/ProductIcons';
import ProductIncludes from '@/pages/order/components/ProductIncludes';
import { isIosNative } from '@/renderer/utils/isIosNative';
import { activatePreviewMode, isPreviewModeActive } from '@/renderer/utils/previewMode';
import { useSafeTopOwner } from '@/renderer/utils/safeTop';
import { IS_SERVER } from '@/renderer/utils/ssr';
import { authToken } from '@/renderer/utils/useMe';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/16/solid';
import { useData } from 'vike-react/useData';
import AllTimeChart from './components/AllTimeChart';
import AppLanding from './components/AppLanding';
import Footer from './components/Footer';
import Header from './components/Header';
import Hero from './components/Hero';
import LogoCloud from './components/LogoCloud';
import OddsScreen from './components/OddsScreen';
import Podcasts from './components/Podcasts';
import ReleaseDemo from './components/ReleaseDemo';
import type { DataType } from './dataFetcher';

/** An offer is visible/purchasable if it hasn't been stripped by the server filter */
function isOfferVisible(offer: any): boolean {
  return offer?.hidden !== true && offer?.available !== false && offer?.price != null;
}

/** A product is orderable if it's available at the product level and has at least one visible offer.
 *  In preview mode, ui.preview=true products are also considered orderable even when available=false. */
function isProductOrderable(
  product: API.MembershipProduct | undefined,
  isPreview = false,
): boolean {
  if (!product) {
    return false;
  }
  if (product.available === false && !(isPreview && (product.ui as any)?.preview === true)) {
    return false;
  }
  return (product.offers ?? []).some(isOfferVisible);
}

/**
 * Route entry. The `/` page differs by platform:
 * - Native, logged in  → redirect to the Release Center (the SPA always
 *   cold-boots at `/`, so a member would otherwise land on the marketing splash).
 * - Native, logged out → a simplified in-app landing (#2039); the full
 *   marketing/sales page feels out of place in an app, and on iOS its purchase
 *   CTAs are all hidden anyway (#2029), leaving a long page with nothing to do.
 * - Web                → the full marketing page, unchanged.
 *
 * This lives in the page component (not a +guard) on purpose: the guard hook
 * doesn't run on the prerendered SPA's first client render, but a component mount
 * always does. It's flash-free — we render nothing while the redirect is in
 * flight. We redirect when a token EXISTS at all (a returning user who has signed
 * in before) rather than only when it's still fresh — an expired-but-refreshable
 * session should still land on content, not the marketing screen; the Release
 * Center guard refreshes.
 */
function IndexRoute() {
  const isNative =
    !IS_SERVER &&
    !!(
      window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }
    ).Capacitor?.isNativePlatform?.();
  const goHome = isNative && !!authToken();

  useEffect(() => {
    if (goHome) {
      navigate('/release-center');
    }
  }, [goHome]);

  if (goHome) {
    return null;
  }

  if (isNative) {
    return <AppLanding />;
  }

  return <Page />;
}

function Page() {
  const data = useData<DataType>();

  // Activate preview mode cookie if URL param is present, so it persists across SPA navigation
  useEffect(() => {
    if (data?.isPreview) {
      activatePreviewMode();
    }
  }, [data?.isPreview]);

  // Availability checks — gate all UI on these
  const isPreview = (data?.isPreview ?? false) || isPreviewModeActive();
  const nflAvailable = isProductOrderable(data?.nflProduct, isPreview);
  const cfbAvailableProducts = (data?.cfbProducts ?? []).filter((p) =>
    isProductOrderable(p, isPreview),
  );
  const cfbAvailable = cfbAvailableProducts.length > 0;

  // Flagship CFB product (the FBS base, most plays) — used for the home card includes list
  const cfbMainProduct = [...cfbAvailableProducts].sort((a, b) => {
    const maxPlays = (p: any) => Math.max(0, ...(p.offers ?? []).map((o: any) => o.plays ?? 0));
    return maxPlays(b) - maxPlays(a);
  })[0];

  // Banner text: only mention sports that are actually orderable
  const bannerSports = [nflAvailable && 'NFL', cfbAvailable && 'CFB'].filter(Boolean).join(' & ');
  // The banner is a web-checkout CTA. In the iOS shell its link opens
  // raspicks.com in the browser instead (US external purchase link, #1864).
  const showBanner = nflAvailable || cfbAvailable;

  // The sticky banner below owns the iOS status-bar inset (its pt), so the
  // layout adds none of its own while it is rendered (#2028).
  useSafeTopOwner(nflAvailable || cfbAvailable);

  return (
    <div className="shrink-0">
      {showBanner && (
        // pt: keep the sticky banner clear of the iOS status bar in the native
        // shell, which paints edge-to-edge (env() is 0 on web); registered as the
        // inset owner above.
        <div className="sticky top-0 z-50 bg-emerald-500 p-2 pt-[calc(0.5rem+var(--safe-top))] text-center font-semibold text-sm text-white">
          🏈 2026 {bannerSports} service{bannerSports.includes('&') ? 's are' : ' is'} now
          available.{' '}
          {isIosNative() ? (
            <SubscribeExternalLink
              path={nflAvailable ? '/order' : '/order/cfb'}
              className="text-white underline hover:text-emerald-100"
            >
              Subscribe at raspicks.com.
            </SubscribeExternalLink>
          ) : (
            <a
              href={nflAvailable ? '/order' : '/order/cfb'}
              className="text-white underline hover:text-emerald-100"
            >
              Join now.
            </a>
          )}
        </div>
      )}
      {/* The banner above already carries the safe-area inset when shown. */}
      <Header safeTop={!showBanner} />
      <Hero nflAvailable={nflAvailable} cfbAvailable={cfbAvailable} />

      {/* Scrolling ticker */}
      {(() => {
        const items = [
          '56% ATS · LAST 3 SEASONS',
          '55% ATS OVER 29 YEARS',
          'BEAT THE CLOSING LINE 90%',
          'VERIFIED @ BETSTAMP',
          'NFL · COLLEGE FOOTBALL',
          'RUN BY PRO BETTORS',
        ];
        const sep = <span className="mx-5 text-[9px] text-emerald-500">◆</span>;
        const content = items.map((item, i) => (
          <span key={i} className="inline-flex items-center">
            {item}
            {sep}
          </span>
        ));
        return (
          <>
            <style>{`@keyframes ticker-scroll{to{transform:translateX(-50%)}} .ticker-track{animation:ticker-scroll 38s linear infinite;} .ticker-track:hover{animation-play-state:paused;}`}</style>
            <div className="overflow-hidden border-y border-white/10 bg-black/25 whitespace-nowrap">
              <div className="ticker-track inline-flex py-2.5 font-mono text-[11.5px] tracking-widest text-zinc-500 uppercase">
                <span className="inline-flex">{content}</span>
                <span className="inline-flex" aria-hidden="true">
                  {content}
                </span>
              </div>
            </div>
          </>
        );
      })()}

      {/* Services section — only rendered if at least one product is orderable */}
      {(nflAvailable || cfbAvailable) && (
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-8 lg:px-16">
          <div className="mb-8 text-center">
            <h2 className="font-bold text-3xl text-white md:text-4xl">Now available</h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {/* NFL card */}
            {nflAvailable && (
              <div className="@container relative flex flex-col gap-4 rounded-sm bg-linear-to-b from-white/5 to-transparent p-6 ring-1 ring-white/20">
                <span className="w-fit rounded-full bg-emerald-500/20 px-2.5 py-1 font-semibold text-emerald-400 text-xs ring-1 ring-emerald-500/40">
                  Available now
                </span>
                <div className="flex items-center gap-3">
                  <NFLIcon className="size-10 shrink-0 fill-white" />
                  <div>
                    <h3 className="font-bold text-white text-xl leading-tight">
                      {data?.nflProduct?.title ?? '2026 NFL Service'}
                    </h3>
                    <p className="mt-0.5 text-sm text-zinc-400">
                      {data?.nflProduct?.ui?.subtitle ??
                        data?.nflProduct?.description ??
                        'Preseason through Super Bowl LXI'}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-3 border-white/10 border-y py-4 @[440px]:grid-cols-3">
                  <div>
                    <div className="font-bold text-lg text-white">153-123</div>
                    <div className="text-xs text-zinc-500">Last 3 seasons</div>
                  </div>
                  <div>
                    <div className="font-bold text-emerald-400 text-lg">55.4%</div>
                    <div className="text-xs text-zinc-500">Win rate</div>
                  </div>
                  <div>
                    <div className="font-bold text-emerald-400 text-lg">+18.22u</div>
                    <div className="text-xs text-zinc-500">Units won</div>
                  </div>
                </div>
                <ProductIncludes
                  product={data?.nflProduct}
                  theme="dark"
                  extraItems={
                    data?.nflProduct?.ui?.releaseTimes?.firstRelease
                      ? [
                          {
                            text: `${
                              data.nflProduct.ui.releaseTimes.firstReleaseText || 'First release'
                            }: ${data.nflProduct.ui.releaseTimes.firstRelease}`,
                          },
                        ]
                      : []
                  }
                />
                {isIosNative() ? (
                  <SubscribeExternalLink
                    path="/order/nfl"
                    className="inline-flex w-full items-center justify-center gap-1.5 rounded-sm bg-emerald-400/10 px-5 py-2.5 font-semibold text-emerald-400 text-sm ring-1 ring-emerald-400 ring-inset hover:bg-emerald-400/20"
                  >
                    Subscribe at raspicks.com
                    <ArrowTopRightOnSquareIcon className="size-4" />
                  </SubscribeExternalLink>
                ) : (
                  <a
                    href="/order/nfl"
                    className="inline-flex w-full items-center justify-center rounded-sm bg-emerald-400/10 px-5 py-2.5 font-semibold text-emerald-400 text-sm ring-1 ring-emerald-400 ring-inset hover:bg-emerald-400/20"
                  >
                    Join now →
                  </a>
                )}
              </div>
            )}

            {/* CFB card */}
            {cfbAvailable && (
              <div className="@container relative flex flex-col gap-4 rounded-sm bg-linear-to-b from-white/5 to-transparent p-6 ring-1 ring-white/20">
                <span className="w-fit rounded-full bg-emerald-500/20 px-2.5 py-1 font-semibold text-emerald-400 text-xs ring-1 ring-emerald-500/40">
                  Available now
                </span>
                <div className="flex items-center gap-3">
                  <CFBIcon className="size-10 shrink-0 fill-white" />
                  <div>
                    <h3 className="font-bold text-white text-xl leading-tight">
                      2026 CFB Services
                    </h3>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {['FBS', 'FCS', 'FBS + FCS'].map((pkg) => (
                        <span
                          key={pkg}
                          className="rounded-full bg-white/10 px-2 py-0.5 text-xs text-zinc-300"
                        >
                          {pkg}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-3 border-white/10 border-y py-4 @[440px]:grid-cols-3">
                  <div>
                    <div className="font-bold text-lg text-white">240-179</div>
                    <div className="text-xs text-zinc-500">Last 3 seasons</div>
                  </div>
                  <div>
                    <div className="font-bold text-emerald-400 text-lg">57.3%</div>
                    <div className="text-xs text-zinc-500">Win rate</div>
                  </div>
                  <div>
                    <div className="font-bold text-emerald-400 text-lg">+45.20u</div>
                    <div className="text-xs text-zinc-500">Units won</div>
                  </div>
                </div>
                <ProductIncludes product={cfbMainProduct} theme="dark" />
                {isIosNative() ? (
                  <SubscribeExternalLink
                    path="/order/cfb"
                    className="inline-flex w-full items-center justify-center gap-1.5 rounded-sm bg-emerald-400/10 px-5 py-2.5 font-semibold text-emerald-400 text-sm ring-1 ring-emerald-400 ring-inset hover:bg-emerald-400/20"
                  >
                    Subscribe at raspicks.com
                    <ArrowTopRightOnSquareIcon className="size-4" />
                  </SubscribeExternalLink>
                ) : (
                  <a
                    href="/order/cfb"
                    className="inline-flex w-full items-center justify-center rounded-sm bg-emerald-400/10 px-5 py-2.5 font-semibold text-emerald-400 text-sm ring-1 ring-emerald-400 ring-inset hover:bg-emerald-400/20"
                  >
                    Join now →
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="mt-12 space-y-8">
        <div className="mx-2 mt-px flex w-auto flex-col items-center justify-center space-y-12 rounded-sm bg-linear-to-b from-white/5 to-transparent p-6 text-center ring-1 ring-white/20 sm:mx-12 sm:p-8">
          <div className="space-y-4">
            <div className="font-semibold text-base text-emerald-500">Market respected</div>
            <h4 className="my-2 font-semibold text-3xl text-white md:text-4xl">
              Picks that disrupt the market and beat the close
            </h4>
            <p className="mx-auto mt-4 max-w-prose text-zinc-400 leading-6">
              When RAS releases a play, sportsbooks will adjust their odds quickly. RAS beats the
              closing line more consistently and by bigger margins than anyone in the space. Even
              for the biggest games in the biggest markets -- like the 2023 Super Bowl.
            </p>
            <p className="mx-auto max-w-prose text-zinc-400 leading-6">
              <a
                className="inline-flex w-full items-center justify-center space-x-1 overflow-hidden rounded-sm px-6 py-3 font-medium text-base text-zinc-200 ring-1 ring-white/10 ring-inset transition hover:bg-white/5 hover:text-white sm:w-auto"
                href="https://www.youtube.com/watch?v=qZ3GxgbcNxI"
                target="_blank"
                rel="noreferrer"
              >
                <span>Watch RAS move the Super Bowl total</span>
                <ArrowTopRightOnSquareIcon height={16} className="h-4 w-4 hover:text-white" />
              </a>
            </p>
          </div>
          <InView triggerOnce threshold={0.2}>
            {({ inView, ref }) => (
              <div
                ref={ref}
                className="-mx-2 max-w-6xl space-y-4 sm:mx-auto sm:px-4 md:px-8 lg:grid lg:max-h-[14rem] lg:grid-cols-12 lg:items-center lg:space-y-0"
              >
                <ReleaseDemo
                  className="relative col-span-4 col-start-1 row-start-1 text-left xl:col-span-5"
                  inView={inView}
                />
                <OddsScreen
                  className="relative col-span-8 col-start-5 row-start-1 xl:col-span-8 xl:col-start-6 xl:ml-8"
                  inView={inView}
                />
              </div>
            )}
          </InView>
        </div>
        <div className="mx-2 grid grid-cols-6 gap-8 sm:mx-12">
          <div className="relative col-span-6 mt-px flex w-auto flex-col justify-center space-y-4 rounded-sm bg-linear-to-b from-white/5 to-transparent p-6 text-center ring-1 ring-white/20 sm:p-8 md:items-start lg:col-span-4">
            <span className="font-semibold text-base text-emerald-500">Long-term winner</span>
            <h4 className="font-semibold text-3xl text-white md:text-left md:text-4xl">
              25+ years of success for subscribers
            </h4>
            <p className="max-w-prose text-zinc-400 md:pb-52 md:text-left">
              With win rates near 56 percent in the short, medium and long term, no one is a bigger
              favorite to win each year.
            </p>
            <AllTimeChart />
          </div>
          <div className="col-span-6 mt-px flex w-auto flex-col items-center justify-center space-y-4 rounded-sm bg-linear-to-b from-white/5 to-transparent p-6 text-center ring-1 ring-white/20 sm:p-8 lg:col-span-2">
            <div className="flex h-12 w-12 flex-col justify-center rounded-full bg-white/10">
              <CheckBadgeIcon height={32} className="m-auto h-8 w-8 text-white" />
            </div>
            <span className="font-semibold text-base text-emerald-500">Independently verified</span>
            <h4 className="font-semibold text-3xl text-white md:text-4xl">
              Plays tracked by a trusted 3rd party
            </h4>
            <p className="max-w-prose text-zinc-400">
              Since 2020, all of our plays have been independently verified by betstamp.
            </p>
            <a
              className="inline-flex items-center justify-center space-x-1 overflow-hidden rounded-sm px-6 py-2 font-medium text-sm text-zinc-200 ring-1 ring-white/10 ring-inset transition hover:bg-white/5 hover:text-white"
              href="https://betstamp.app/u/RAS"
              target="_blank"
              rel="noreferrer"
            >
              <span>View betstamp profile</span>
              <ArrowUpRightIcon height={16} className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>
      <Podcasts />
      <div
        className="-z-10 absolute inset-x-0 top-[calc(100%-13rem)] transform-gpu overflow-hidden blur-3xl sm:top-[calc(100%-30rem)]"
        aria-hidden="true"
      ></div>
      <div className="mx-auto max-w-6xl text-center">
        <LogoCloud />
      </div>

      {/* Get the app — install CTA (Android live; iOS badge slots in later) */}
      <div className="mx-2 mt-16 flex flex-col items-center justify-center space-y-6 rounded-sm bg-linear-to-b from-white/5 to-transparent p-8 text-center ring-1 ring-white/20 sm:mx-12">
        <div className="space-y-3">
          <div className="font-semibold text-base text-emerald-500">Now on your phone</div>
          <h4 className="font-semibold text-3xl text-white md:text-4xl">Get the RAS app</h4>
          <p className="mx-auto max-w-prose text-zinc-400 leading-6">
            Get a heads-up before every release, so you&apos;re ready to bet the moment the plays
            drop. Install it free from Google Play.
          </p>
        </div>
        <AppStoreBadges className="justify-center" />
      </div>

      <Footer />
    </div>
  );
}
